import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useShop } from '../../../shop/ShopProvider';
import {
  detectPreset,
  isIsoDate,
  normalizeRange,
  presetRange,
  suggestInterval,
  todayIn,
  type DateRange,
  type Interval,
  type Preset,
  describeRange,
} from '../lib/dateRange';

/** The shop's today (business date in its time zone). */
export function useToday(): string {
  const { format } = useShop();
  return todayIn(format.timeZone);
}

/**
 * Report filters kept in the URL (UI guideline: filters live in the URL, so a report can be
 * bookmarked or shared). Presets are stored by name so "Last 30 days" stays relative.
 */
export function useReportParams(defaultPreset: Exclude<Preset, 'custom'> = 'thisMonth') {
  const [params, setParams] = useSearchParams();
  const today = useToday();

  const presetParam = params.get('range');
  const fromParam = params.get('from');
  const toParam = params.get('to');

  const range: DateRange = useMemo(() => {
    if (presetParam === 'custom' && isIsoDate(fromParam) && isIsoDate(toParam))
      return normalizeRange({ from: fromParam, to: toParam });
    const p = (presetParam ?? defaultPreset) as Exclude<Preset, 'custom'>;
    try {
      return presetRange(p, today);
    } catch {
      return presetRange(defaultPreset, today);
    }
  }, [presetParam, fromParam, toParam, defaultPreset, today]);

  const preset: Preset = presetParam === 'custom' ? 'custom' : detectPreset(range, today);
  const intervalParam = params.get('interval');
  const interval: Interval =
    intervalParam === 'day' || intervalParam === 'week' || intervalParam === 'month'
      ? intervalParam
      : suggestInterval(range);
  const page = Math.max(1, Number(params.get('page')) || 1);
  const pageSize = [25, 50, 100].includes(Number(params.get('pageSize')))
    ? Number(params.get('pageSize'))
    : 25;

  const update = useCallback(
    (changes: Record<string, string | null>, resetPage = true) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          if (resetPage && !('page' in changes)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    today,
    range,
    preset,
    interval,
    page,
    pageSize,
    get: (key: string) => params.get(key),
    update,
    setPreset: (p: Exclude<Preset, 'custom'>) =>
      update({ range: p, from: null, to: null, interval: null }),
    setCustom: (r: DateRange) => {
      const n = normalizeRange(r);
      update({ range: 'custom', from: n.from, to: n.to, interval: null });
    },
    setInterval: (i: Interval) => update({ interval: i }, false),
    setPage: (p: number) => update({ page: p === 1 ? null : String(p) }, false),
    setPageSize: (s: number) => update({ pageSize: s === 25 ? null : String(s) }),
  };
}

export type ReportParams = ReturnType<typeof useReportParams>;

/** The report's range as text, e.g. "1 – 20 Oct 2026" (print header). */
export function usePeriodText(p: ReportParams): string {
  const { format } = useShop();
  return describeRange(p.range, format.locale);
}
